import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCookies } from "react-cookie";

import {
    CustomerListType,
    DepartmentSimpleType,
    SecretarySimpleType,
    SelectedParametersType,
} from "../../../../types/types";

import customerFindAllListResponse from "../../../../resources/Api/customer/customerFindAllListResponse";
import findAllSecretariesByCustomerIdListResponse from "../../../../resources/Api/secretary/findAllSecretariesByCustomerIdListResponse";
import findAllDepartmentsByCustomerIdAndSecretaryIdListResponse from "../../../../resources/Api/department/findAllDepartmentsByCustomerIdAndSecretaryIdListResponse";
import findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse from "../../../../resources/Api/vehicle/findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse";
import transferVehiclesToCustomer from "../../../../resources/Api/vehicle/transferVehiclesToCustomer";
import findAllVehicleCustomerTransfers, { VehicleCustomerTransferType } from "../../../../resources/Api/vehicle/findAllVehicleCustomerTransfers";

import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    CloseOutlined,
    HistoryOutlined,
    SaveOutlined,
    SearchOutlined,
    UndoOutlined,
} from "@mui/icons-material";

import { setToastRefToOpen } from "../../../../functions/functions";

import { Toast } from "primereact/toast";

// Mantido igual às constantes de VehicleCustomerTransferRequestModel na API.
const JUSTIFICATION_MIN_LENGTH = 30;
const JUSTIFICATION_MAX_LENGTH = 1000;
const MAX_VEHICLES_PER_TRANSFER = 50;

type VehicleOptionType = {
    id: number;
    name: string;
    vehiclePlate: string;
};

type TransferVehicleCustomerComponentProps = {
    initialSourceCustomerId?: number;
    initialVehicleId?: number;
};

const TransferVehicleCustomerComponent = ({ initialSourceCustomerId, initialVehicleId }: TransferVehicleCustomerComponentProps) => {
    // cookies
    const [cookies] = useCookies([
        "selectedParameters",
        "token",
    ]);

    const currentParameters: SelectedParametersType = useMemo(() => cookies.selectedParameters, [cookies.selectedParameters]);
    const getToken = useCallback((): string => cookies.token || sessionStorage.getItem("token"), [cookies.token]);

    const toastRef = useRef<Toast>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);

    // origem
    const [customersList, setCustomersList] = useState<Array<CustomerListType>>([]);
    const [sourceCustomer, setSourceCustomer] = useState<CustomerListType | null>(null);
    const [vehiclesList, setVehiclesList] = useState<Array<VehicleOptionType>>([]);
    const [vehicles, setVehicles] = useState<Array<VehicleOptionType>>([]);

    // destino
    const [targetCustomer, setTargetCustomer] = useState<CustomerListType | null>(null);
    const [secretariesList, setSecretariesList] = useState<Array<SecretarySimpleType>>([]);
    const [targetSecretary, setTargetSecretary] = useState<SecretarySimpleType | null>(null);
    const [departmentsList, setDepartmentsList] = useState<Array<DepartmentSimpleType>>([]);
    const [targetDepartment, setTargetDepartment] = useState<DepartmentSimpleType | null>(null);

    const [justification, setJustification] = useState<string>("");
    const [justificationTouched, setJustificationTouched] = useState<boolean>(false);

    // histórico
    const [isHistoryOpen, setIsHistoryOpen] = useState<boolean>(false);
    const [history, setHistory] = useState<Array<VehicleCustomerTransferType>>([]);
    const [historyTotal, setHistoryTotal] = useState<number>(0);
    const [historyPage, setHistoryPage] = useState<number>(0);
    const [historySize, setHistorySize] = useState<number>(5);
    const [historySearch, setHistorySearch] = useState<string>("");
    const [historyQuery, setHistoryQuery] = useState<string>("");

    const showToast = useCallback((message: string, status: "warn" | "success" | "info" | "error") => {
        setToastRefToOpen(toastRef, message, status === "success" ? "Sucesso!" : "Atenção!", status, undefined, 5000, false);
    }, []);

    const justificationLength = justification.trim().length;
    const isJustificationValid = justificationLength >= JUSTIFICATION_MIN_LENGTH && justificationLength <= JUSTIFICATION_MAX_LENGTH;
    const isImpersonating = localStorage.getItem("impersonating") === "true";

    const canSubmit = !!sourceCustomer
        && vehicles.length > 0
        && vehicles.length <= MAX_VEHICLES_PER_TRANSFER
        && !!targetCustomer
        && !!targetDepartment
        && isJustificationValid
        && !isImpersonating
        && !loading;

    const getCustomersList = useCallback(async () => {
        try {
            const response = await customerFindAllListResponse(getToken(), currentParameters.customer.id);
            setCustomersList(response.data);
        } catch (error) {
            console.log(error);
            setCustomersList([]);
        }
    }, [getToken, currentParameters?.customer?.id]);

    useEffect(() => {
        getCustomersList();
    }, [getCustomersList]);

    // Atalho da tela de veículos: abre com o cliente e o veículo de lá já selecionados (uma vez só,
    // para não refazer a seleção se o usuário trocar depois).
    const appliedInitialCustomer = useRef<boolean>(false);
    const appliedInitialVehicle = useRef<boolean>(false);

    useEffect(() => {
        if (appliedInitialCustomer.current || !initialSourceCustomerId || initialSourceCustomerId <= 0) return;
        const customer = customersList.find((item) => item.id === initialSourceCustomerId);
        if (customer) {
            appliedInitialCustomer.current = true;
            setSourceCustomer(customer);
        }
    }, [initialSourceCustomerId, customersList]);

    useEffect(() => {
        if (appliedInitialVehicle.current || !initialVehicleId) return;
        const vehicle = vehiclesList.find((item) => item.id === initialVehicleId);
        if (vehicle) {
            appliedInitialVehicle.current = true;
            setVehicles([vehicle]);
        }
    }, [initialVehicleId, vehiclesList]);

    useEffect(() => {
        setVehicles([]);
        setVehiclesList([]);
        if (!sourceCustomer) return;

        findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse(getToken(), sourceCustomer.id, -1, true)
            .then((response) => setVehiclesList(response.data))
            .catch((error) => {
                console.log(error);
                setVehiclesList([]);
            });
    }, [sourceCustomer, getToken]);

    useEffect(() => {
        setTargetSecretary(null);
        setSecretariesList([]);
        if (!targetCustomer) return;

        findAllSecretariesByCustomerIdListResponse(getToken(), targetCustomer.id)
            .then((response) => setSecretariesList(response.data))
            .catch((error) => {
                console.log(error);
                setSecretariesList([]);
            });
    }, [targetCustomer, getToken]);

    useEffect(() => {
        setTargetDepartment(null);
        setDepartmentsList([]);
        if (!targetCustomer || !targetSecretary) return;

        findAllDepartmentsByCustomerIdAndSecretaryIdListResponse(getToken(), targetCustomer.id, targetSecretary.id)
            .then((response) => setDepartmentsList(response.data))
            .catch((error) => {
                console.log(error);
                setDepartmentsList([]);
            });
    }, [targetCustomer, targetSecretary, getToken]);

    const getHistory = useCallback(async () => {
        try {
            const response = await findAllVehicleCustomerTransfers(getToken(), historyQuery, historyPage, historySize);
            setHistory(response.data.content);
            setHistoryTotal(response.data.totalElements);
        } catch (error) {
            console.log(error);
            setHistory([]);
            setHistoryTotal(0);
        }
    }, [getToken, historyQuery, historyPage, historySize]);

    // Só busca com o modal aberto; reabrir recarrega (pega transferências feitas nesse meio tempo).
    useEffect(() => {
        if (isHistoryOpen) getHistory();
    }, [isHistoryOpen, getHistory]);

    const handleClear = useCallback(() => {
        setSourceCustomer(null);
        setVehicles([]);
        setTargetCustomer(null);
        setJustification("");
        setJustificationTouched(false);
    }, []);

    const onTransfer = useCallback(async () => {
        if (!sourceCustomer || !targetCustomer || !targetDepartment) return;

        setLoading(true);
        try {
            await transferVehiclesToCustomer(getToken(), {
                sourceCustomerId: sourceCustomer.id,
                vehicleIds: vehicles.map((vehicle) => vehicle.id),
                targetCustomerId: targetCustomer.id,
                targetDepartmentId: targetDepartment.id,
                justification: justification.trim(),
            });
            const plural = vehicles.length > 1;
            showToast(`Veículo${plural ? "s" : ""} transferido${plural ? "s" : ""} com sucesso.`, "success");
            setIsConfirmOpen(false);
            handleClear();
            setHistoryPage(0);
        } catch (error: any) {
            setIsConfirmOpen(false);
            showToast(error?.response?.data?.reason || "Não foi possível transferir, tente novamente.", "warn");
        } finally {
            setLoading(false);
        }
    }, [sourceCustomer, targetCustomer, targetDepartment, vehicles, justification, getToken, showToast, handleClear]);

    const vehicleLabel = (vehicle: VehicleOptionType) =>
        vehicle.name && vehicle.name !== vehicle.vehiclePlate ? `${vehicle.vehiclePlate} — ${vehicle.name}` : vehicle.vehiclePlate;

    const customerLabel = (customer: CustomerListType | null) => customer?.reducedName || customer?.name || "";

    const justificationHelper = justificationTouched && justificationLength < JUSTIFICATION_MIN_LENGTH
        ? `Mínimo de ${JUSTIFICATION_MIN_LENGTH} caracteres (faltam ${JUSTIFICATION_MIN_LENGTH - justificationLength}).`
        : `${justificationLength}/${JUSTIFICATION_MAX_LENGTH} caracteres — mínimo ${JUSTIFICATION_MIN_LENGTH}.`;

    return (
        <>
            <Toast ref={toastRef} position="center" />
            <Box className="container-fluid tw-w-full tw-rounded-xl tw-bg-white tw-px-0 tw-py-2" sx={{ width: "100%" }}>
                <Alert severity="warning" sx={{ fontFamily: "Prometo", mb: 2, borderRadius: 2 }}>
                    Ação administrativa. O veículo é inativado no cliente de origem, que mantém todo o histórico,
                    e um novo cadastro é criado no departamento de destino com os mesmos rastreadores.
                    Vínculos com condutores, grupos, rotas e cercas do cliente de origem são desfeitos.
                    Número de contrato e valores de cobrança não são copiados.
                </Alert>

                <Paper elevation={0} sx={style}>
                    <Typography sx={sectionTitle}>Origem</Typography>
                    <Autocomplete
                        options={customersList}
                        value={sourceCustomer}
                        onChange={(_, option) => setSourceCustomer(option)}
                        getOptionLabel={customerLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText={"Nada encontrado"}
                        renderInput={(params) => (
                            <TextField {...params} label="Cliente de origem" placeholder="Selecione um cliente" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                    <Autocomplete
                        multiple
                        disabled={!sourceCustomer}
                        options={vehiclesList}
                        value={vehicles}
                        onChange={(_, options) => setVehicles(options)}
                        getOptionLabel={vehicleLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterSelectedOptions
                        noOptionsText={"Nada encontrado"}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Veículos"
                                placeholder={vehicles.length ? "" : "Busque pela placa ou nome"}
                                required
                                InputLabelProps={{ shrink: true }}
                                error={vehicles.length > MAX_VEHICLES_PER_TRANSFER}
                                helperText={vehicles.length > MAX_VEHICLES_PER_TRANSFER ? `Transfira no máximo ${MAX_VEHICLES_PER_TRANSFER} veículos por vez.` : undefined}
                            />
                        )}
                    />

                    <Typography sx={sectionTitle}>Destino</Typography>
                    <Autocomplete
                        disabled={!sourceCustomer}
                        options={customersList.filter((customer) => customer.id !== sourceCustomer?.id)}
                        value={targetCustomer}
                        onChange={(_, option) => setTargetCustomer(option)}
                        getOptionLabel={customerLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText={"Nada encontrado"}
                        renderInput={(params) => (
                            <TextField {...params} label="Cliente de destino" placeholder="Selecione um cliente" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                    <Autocomplete
                        disabled={!targetCustomer}
                        options={secretariesList}
                        value={targetSecretary}
                        onChange={(_, option) => setTargetSecretary(option)}
                        getOptionLabel={(option) => option.reducedName || option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText={"Nada encontrado"}
                        renderInput={(params) => (
                            <TextField {...params} label="Secretaria" placeholder="Selecione uma secretaria" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                    <Autocomplete
                        disabled={!targetSecretary}
                        options={departmentsList}
                        value={targetDepartment}
                        onChange={(_, option) => setTargetDepartment(option)}
                        getOptionLabel={(option) => option.reducedName || option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText={"Nada encontrado"}
                        renderInput={(params) => (
                            <TextField {...params} label="Departamento" placeholder="Selecione um departamento" required InputLabelProps={{ shrink: true }} />
                        )}
                    />

                    <Typography sx={sectionTitle}>Justificativa</Typography>
                    <TextField
                        sx={{ gridColumn: "1 / -1" }}
                        label="Justificativa"
                        placeholder="Ex.: Correção de placa alocada no cliente errado; troca de veículos entre clientes do mesmo grupo empresarial; veículo adquirido em leilão por outro cliente."
                        required
                        multiline
                        minRows={4}
                        value={justification}
                        onChange={(event) => setJustification(event.target.value)}
                        onBlur={() => setJustificationTouched(true)}
                        inputProps={{ maxLength: JUSTIFICATION_MAX_LENGTH }}
                        InputLabelProps={{ shrink: true }}
                        error={justificationTouched && !isJustificationValid}
                        helperText={justificationHelper}
                    />
                </Paper>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                    <Button
                        type="button"
                        onClick={() => setIsHistoryOpen(true)}
                        className="btn btn-primary btn-block login-button"
                        sx={{ minWidth: 120, minHeight: 40, borderRadius: 2, backgroundColor: "#09414A", boxShadow: "none", "&:hover": { backgroundColor: "#06303A" } }}
                        style={{ fontFamily: "Prometo", color: "#ffff", margin: 5 }}
                    >
                        <HistoryOutlined style={{ marginRight: 5 }} />
                        Histórico
                    </Button>
                    <Button
                        type="button"
                        onClick={handleClear}
                        className="btn btn-primary btn-block login-button-close"
                        sx={{ minWidth: 110, minHeight: 40, borderRadius: 2, boxShadow: "none" }}
                        style={{ fontFamily: "Prometo", color: "#ffff", margin: 5 }}
                    >
                        <UndoOutlined style={{ marginRight: 5 }} />
                        Limpar
                    </Button>
                    <Button
                        type="button"
                        onClick={() => setIsConfirmOpen(true)}
                        disabled={!canSubmit}
                        className="btn btn-primary btn-block login-button"
                        sx={{ minWidth: 120, minHeight: 40, borderRadius: 2, backgroundColor: "#00B700", boxShadow: "none", "&:hover": { backgroundColor: "#009900" } }}
                        style={{ fontFamily: "Prometo", color: "#ffff", margin: 5 }}
                    >
                        <SaveOutlined style={{ marginRight: 5 }} />
                        Transferir
                    </Button>
                </Box>
            </Box>

            <Dialog open={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontFamily: "Prometo", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography component="span" sx={{ ...sectionTitle, fontSize: 18 }}>Histórico de transferências</Typography>
                    <IconButton onClick={() => setIsHistoryOpen(false)} size="small">
                        <CloseOutlined />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ fontFamily: "Prometo" }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                        <TextField
                            size="small"
                            placeholder="Placa ou cliente"
                            value={historySearch}
                            onChange={(event) => setHistorySearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    setHistoryPage(0);
                                    setHistoryQuery(historySearch.trim());
                                }
                            }}
                            InputProps={{ endAdornment: <SearchOutlined fontSize="small" /> }}
                        />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Placa</TableCell>
                                    <TableCell>Origem</TableCell>
                                    <TableCell>Destino</TableCell>
                                    <TableCell>Usuário</TableCell>
                                    <TableCell>Justificativa</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">Nenhuma transferência encontrada.</TableCell>
                                    </TableRow>
                                ) : history.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{item.createdAt}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{item.vehiclePlate}</TableCell>
                                        <TableCell>{item.sourceCustomerName}{item.sourceDepartmentName ? ` / ${item.sourceDepartmentName}` : ""}</TableCell>
                                        <TableCell>{item.targetCustomerName}{item.targetDepartmentName ? ` / ${item.targetDepartmentName}` : ""}</TableCell>
                                        <TableCell>{item.userName ?? "-"}</TableCell>
                                        <TableCell sx={{ maxWidth: 280 }}>
                                            <Tooltip title={item.justification} arrow>
                                                <Typography noWrap sx={{ fontSize: 14 }}>{item.justification}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={historyTotal}
                        page={historyPage}
                        onPageChange={(_, page) => setHistoryPage(page)}
                        rowsPerPage={historySize}
                        onRowsPerPageChange={(event) => {
                            setHistorySize(Number(event.target.value));
                            setHistoryPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25]}
                        labelRowsPerPage="Por página"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={isConfirmOpen} onClose={() => !loading && setIsConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "Prometo" }}>Confirmar transferência</DialogTitle>
                <DialogContent sx={{ fontFamily: "Prometo" }}>
                    <Typography sx={{ mb: 1 }}>
                        <b>{vehicles.length}</b> veículo{vehicles.length > 1 ? "s" : ""} de <b>{customerLabel(sourceCustomer)}</b> para{" "}
                        <b>{customerLabel(targetCustomer)}</b> / {targetDepartment?.reducedName || targetDepartment?.name}:
                    </Typography>
                    <Typography sx={{ mb: 2, fontSize: 14 }}>{vehicles.map((vehicle) => vehicle.vehiclePlate).join(", ")}</Typography>
                    <Typography sx={{ fontSize: 14, color: "text.secondary", whiteSpace: "pre-wrap" }}>{justification.trim()}</Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta ação não pode ser desfeita automaticamente. O cliente de origem deixa de ver o veículo como ativo.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmOpen(false)} disabled={loading}>Cancelar</Button>
                    <Button onClick={onTransfer} disabled={loading} variant="contained" color="success">
                        {loading ? "Transferindo..." : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const sectionTitle = {
    fontFamily: "Prometo",
    fontWeight: 700,
    fontSize: 15,
    color: "#09414A",
    gridColumn: "1 / -1",
};

const style = {
    bgcolor: "#ffff",
    width: "100%",
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
    gap: 1.5,
    border: "none",
    boxShadow: "none",
    borderRadius: 3,
    padding: { xs: 1, sm: 1.5 },
    fontFamily: "Prometo",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700", borderWidth: "2px" },
};

export default TransferVehicleCustomerComponent;
